"use client";

/**
 * Success view shown after successful activity registration or waitlist join.
 * Shows the (masked) confirmation email destination.
 */

import type React from "react";
import { AlertCircle, Check, Clock } from "lucide-react";
import { FetchButton } from "@/fetch-ui/FetchButton";
import { CalendarAddButtons } from "./CalendarAddButtons";
import type { CalendarSlotInfo } from "./CalendarAddButtons";

/** Signup mode determines the success messaging */
export type SignupMode = "register" | "waitlist";

/** Data describing what the attendee just completed. */
export interface SignupRegistrationInfo {
  mode: SignupMode;
  activityTitle: string;
  confirmationMessage: string | null;
  /** Email the confirmation was sent to. When provided, shown masked with an edit link. */
  email?: string;
  /**
   * When a multi-slot submission produces mixed outcomes, show per-bucket counts
   * (e.g. "2 registered, 1 waitlisted") so the user knows where each slot landed.
   */
  multiSlotBreakdown?: { registered: number; waitlisted: number };
  /** If the multi-slot submission had partial failures, surface a banner explaining what's missing. */
  partialFailure?: { succeeded: number; total: number; reason?: string };
}

interface SignupSuccessViewProps {
  /** Registration data to display. */
  registration: SignupRegistrationInfo;
  /** Slot details for "Add to Calendar" links. Only shown for confirmed (non-waitlist) single-slot signups. */
  slotInfo?: CalendarSlotInfo;
  /** Dismiss handler for the inline "Done" button. */
  onDone: () => void;
}

/** Mask a user email for display: "jane@example.com" → "j***@example.com". */
function maskEmail(email: string): string {
  const trimmed = email.trim();
  if (!trimmed.includes("@")) return trimmed;
  const [local, domain] = trimmed.split("@");
  if (local.length <= 1) return trimmed;
  return `${local[0]}***@${domain}`;
}

/** Success view shown after successful registration or waitlist join */
export function SignupSuccessView({ registration, slotInfo, onDone }: SignupSuccessViewProps): React.ReactElement {
  const { mode, activityTitle, confirmationMessage, email, multiSlotBreakdown, partialFailure } = registration;
  const showEmailRecovery = Boolean(email);
  const isWaitlist = mode === "waitlist";
  const isMixed = Boolean(multiSlotBreakdown && multiSlotBreakdown.registered > 0 && multiSlotBreakdown.waitlisted > 0);

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className={`flex h-12 w-12 items-center justify-center rounded-full ${
          isWaitlist ? "bg-amber-100" : "bg-green-100"
        }`}
      >
        {isWaitlist ? (
          <Clock className="h-6 w-6 text-amber-600" strokeWidth={2.5} aria-hidden="true" />
        ) : (
          <Check className="h-6 w-6 text-green-600" strokeWidth={3} aria-hidden="true" />
        )}
      </div>
      <div className="flex flex-col items-center gap-1 text-center">
        <h3 className="text-lg font-semibold text-gray-900">
          {isMixed ? "You're all set!" : mode === "waitlist" ? "You're on the waitlist!" : "You're signed up!"}
        </h3>
        <p className="text-sm font-medium text-gray-600">
          {mode === "waitlist"
            ? `We'll email you if a spot opens up for ${activityTitle}.`
            : confirmationMessage || `You're registered for ${activityTitle}.`}
        </p>
        {multiSlotBreakdown && (multiSlotBreakdown.registered > 0 || multiSlotBreakdown.waitlisted > 0) && (
          <p className="text-sm font-medium text-gray-500" role="status">
            {multiSlotBreakdown.registered > 0 && (
              <>
                {multiSlotBreakdown.registered}{" "}
                {multiSlotBreakdown.registered === 1 ? "slot registered" : "slots registered"}
              </>
            )}
            {multiSlotBreakdown.registered > 0 && multiSlotBreakdown.waitlisted > 0 && " · "}
            {multiSlotBreakdown.waitlisted > 0 && (
              <>
                {multiSlotBreakdown.waitlisted}{" "}
                {multiSlotBreakdown.waitlisted === 1 ? "slot waitlisted" : "slots waitlisted"}
              </>
            )}
          </p>
        )}
      </div>

      {partialFailure && (
        <div className="flex w-full max-w-sm items-start gap-2 rounded-lg bg-amber-50 p-3">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
          <p className="text-sm font-medium text-amber-800">
            Signed up for {partialFailure.succeeded} of {partialFailure.total} slots.
            {partialFailure.reason ? ` ${partialFailure.reason}` : " One or more slots were unavailable."}
          </p>
        </div>
      )}

      {showEmailRecovery && email && (
        <div className="flex w-full flex-col items-center gap-0.5 text-center">
          <p className="text-xs font-semibold text-gray-500">
            {isWaitlist ? "Waitlist confirmation sent to " : "Confirmation sent to "}
            <span className="text-gray-800">{maskEmail(email)}</span>
          </p>
          <p className="text-xs font-medium text-gray-400">Check your spam folder if you don&apos;t see it.</p>
        </div>
      )}

      {slotInfo && <CalendarAddButtons slotInfo={slotInfo} />}

      <FetchButton variant="primary" size="md" onClick={onDone} className="mt-1 w-full touch-manipulation">
        Done
      </FetchButton>
    </div>
  );
}
